import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { ButtonForm, CustomTextField } from 'Components/Form/styles';
import { Column, Row } from 'Components/Grid';
import { MuiChipsInput } from 'mui-chips-input';
import { Controller } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUsersName } from 'services/users-service';
import { useAuthTenant } from 'store/auth';

const criticityColors = {
  '1': '#22c55e',
  '2': '#84cc16',
  '3': '#eab308',
  '4': '#f97316',
  '5': '#ef4444'
};

export const Detalhes = ({ handleNext, register, control, handleSubmit, errors, getValues, id }) => {
  const { t } = useTranslation(['asm']);
  const selectedTenant = useSelector(useAuthTenant);
  const navigate = useNavigate();
  const onSubmit = () => {
    handleNext();
  };

  const { data: users } = useQuery(['getUsers'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data?.length > 0 ? result?.data : [];
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-100">
      <Row>
        <Column xs={12} md={6} className="d-flex">
          <div data-tour="area-name" style={{ width: '100%' }}>
            <CustomTextField
              {...register('name', { required: t('asm:areas.form.name.required') })}
              label={t('asm:areas.form.name.label')}
              autoFocus
              error={errors?.name ? true : false}
              sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
              helperText={errors?.name ? errors.name.message : null}
              InputLabelProps={{ shrink: true }}
              disabled={!!id}
            />
          </div>
        </Column>
        <Column xs={12} md={6} className="d-flex">
          <div data-tour="area-criticity" style={{ width: '100%' }}>
            <Controller
              control={control}
              rules={{
                validate: () => {
                  return getValues('criticity') !== '';
                },
                required: t('asm:areas.form.criticality.required'),
              }}
              name="criticity"
            render={({ field: { onChange, value } }) => (
              <FormControl fullWidth sx={{ marginBottom: '20px' }} error={errors?.criticity ? true : false}>
                <InputLabel id="criticity-label" shrink>
                  {t('asm:areas.form.criticality.label')}
                </InputLabel>
                <Select
                  labelId="criticity-label"
                  id="criticity"
                  value={value}
                  notched
                  label={t('asm:areas.form.criticality.label')}
                  onChange={onChange}
                  sx={{
                    '.MuiSelect-select': {
                      padding: '13px 14px',
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) return t('asm:areas.form.criticality.placeholder');
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: criticityColors[selected] || '#6b7280',
                            border: '1px solid rgba(0,0,0,0.1)'
                          }}
                        />
                        <span>{t('asm:criticality.' + selected)}</span>
                      </div>
                    );
                  }}
                >
                  <MenuItem value="" key={-1}>
                    {t('asm:areas.form.criticality.placeholder')}
                  </MenuItem>
                  {['1', '2', '3', '4', '5'].map((item, index) => (
                    <MenuItem value={item} key={index}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: criticityColors[item] || '#6b7280',
                            border: '1px solid rgba(0,0,0,0.1)'
                          }}
                        />
                        <span>{t('asm:criticality.' + item)}</span>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors?.criticity ? errors.criticity.message : ''}</FormHelperText>
              </FormControl>
            )}
          />
          </div>
        </Column>
      </Row>
      <Row>
        <Column xs={12} md={6} className="d-flex">
          <div data-tour="area-user" style={{ width: '100%' }}>
            <Controller
              control={control}
              rules={{
                validate: () => {
                  return getValues('user') !== '';
                },
                required: t('asm:areas.form.responsible.required'),
              }}
              name="user"
            render={({ field: { onChange, value } }) => (
              <FormControl fullWidth sx={{ marginBottom: '24px' }} error={errors?.user ? true : false}>
                <InputLabel id="user-label" shrink={true}>
                  {t('asm:areas.form.responsible.label')}
                </InputLabel>
                <Select labelId="user-label" id="user" value={value} label={t('asm:areas.form.responsible.label')} onChange={onChange} notched>
                  <MenuItem value="" key={-1}>
                    {t('asm:areas.form.responsible.placeholder')}
                  </MenuItem>
                  {users
                    ?.map(user => user.name)
                    .map((item, index) => (
                      <MenuItem value={`${item}`} key={`user-${index}`}>
                        {item}
                      </MenuItem>
                    ))}
                </Select>
                <FormHelperText>{errors?.user ? errors.user.message : ''}</FormHelperText>
              </FormControl>
            )}
          />
          </div>
        </Column>
        <Column xs={12} md={6} className="d-flex">
          <FormControl fullWidth sx={{ margin: '0 auto 24px' }} data-tour="area-tags">
            <Controller
              control={control}
              name="tags"
              render={({ field: { onChange, value } }) => (
                <MuiChipsInput
                  label={t('asm:areas.form.tags.label')}
                  value={value}
                  onChange={onChange}
                  InputLabelProps={{ shrink: true }}
                  placeholder={t('asm:areas.form.tags.placeholder')}
                  renderChip={(Component, props) => {
                    return (
                      <Component
                        {...props}
                        variant="outlined"
                        color="primary"
                        sx={{
                          width: 'fit-content',
                        }}
                      />
                    );
                  }}
                  sx={{
                    '& .MuiButtonBase-root.MuiChip-root.MuiChip-filled': {
                      width: 'fit-content',
                    },
                    '& .MuiButtonBase-root.MuiIconButton-root': {
                      width: 'fit-content',
                      marginTop: '0px',
                    },
                  }}
                />
              )}
            ></Controller>
          </FormControl>
        </Column>
      </Row>
      <Row>
        <Column>
          <div data-tour="area-description" style={{ width: '100%' }}>
            <TextField
              {...register('description', {
                required: t('asm:areas.form.description.required'),
              })}
              label={t('asm:areas.form.description.label')}
              multiline
              rows={4}
              error={errors?.description ? true : false}
              helperText={errors?.description ? errors.description.message : null}
              sx={{ margin: '0 auto 24px', width: '100%' }}
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </Column>
      </Row>
      <Row justifyContent="end" alignItems="center">
        <Column xs={4} md={3} className="d-flex" style={{ gap: '8px', justifyContent: 'flex-end' }}>
          <div data-tour="form-actions" style={{ display: 'flex', gap: '8px', width: '100%', justifyContent: 'flex-end' }}>
            <ButtonForm secondary id="cancel" label={t('asm:areas.form.cancel')} onClick={() => navigate('/areas')}></ButtonForm>
            <ButtonForm label={t('asm:areas.form.save')} type="submit" />
          </div>
        </Column>
      </Row>
    </form>
  );
};
